import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../../app/store";
import {
    ConfigurationRegion,
    CreateLocationRequest,
    EnquiryProvider,
    LocationInListDTO,
    VehicleDataProvider
} from "../../../api/generated";
import {v4} from "uuid";
import {WorkingHoursRequest} from "./modal/createAndUpdate/LocationForm";
import {locationApi, RequestStatus} from "../../../api/apiConfiguration";
import {selectExceptionally} from "../../../utils/selectExceptionally";
import {prepareEmails} from "../assistant/botCreateSlice";

export const createLocation = createAsyncThunk<LocationInListDTO, undefined, { state: RootState }>(
    "locationCreate/create",
    async (_payload, {getState, rejectWithValue}) => {
        const {locationCreate} = getState();
        const {createLocationRequest} = locationCreate;
        const request = mapRequest(createLocationRequest);
        try {
            return await locationApi.createLocation({createLocationRequest: request});
        } catch (error: any) {
            const message = error?.response ? await error.response.text() : null;
            return rejectWithValue(message);
        }
    }
);

export type CreateLocationRequestUI =
    Omit<CreateLocationRequest, "locationEmailTranscriptRecipients" | "aftersalesEmailTranscriptRecipients">
    & {
    locationEmailTranscriptRecipients: string;
    aftersalesEmailTranscriptRecipients: string;
}
export type CreateLocationRequestPartial = Partial<CreateLocationRequestUI>

export interface CreateLocationState {
    createLocationRequest: CreateLocationRequestPartial
    createLocationRequestStatus: RequestStatus
    errorMessage: string | null
}

const defaultWorkingHours = {
    openTime: {
        hour: 9,
        minute: 0
    },
    closeTime: {
        hour: 18,
        minute: 0
    },
    isClosed: false
}

const buildDefaultCreateLocationRequest = (): CreateLocationRequestPartial => {
    return {
        publicId: v4(),
        vehicleDataProvider: VehicleDataProvider.NoProvider,
        enquiryProvider: EnquiryProvider.NoProvider,
        em2Region: ConfigurationRegion.Uk,
        workSchedule: {
            monday: defaultWorkingHours,
            tuesday: defaultWorkingHours,
            wednesday: defaultWorkingHours,
            thursday: defaultWorkingHours,
            friday: defaultWorkingHours,
            saturday: defaultWorkingHours,
            sunday: defaultWorkingHours,
        },
        locationEmailTranscriptRecipients: "",
        locationEmailFiltrationEnabled: false,
        locationEmailTranscriptSummaryEnabled: false,
        aftersalesEnabled: false,
        aftersalesEmailTranscriptRecipients: "",
        aftersalesEmailFiltrationEnabled: false,
        aftersalesEmailTranscriptSummaryEnabled: false,
        supportedVehicleManufacturers: {},
        aftersalesWorkSchedule: {
            monday: defaultWorkingHours,
            tuesday: defaultWorkingHours,
            wednesday: defaultWorkingHours,
            thursday: defaultWorkingHours,
            friday: defaultWorkingHours,
            saturday: defaultWorkingHours,
            sunday: defaultWorkingHours,
        }
    }
}

const buildInitialState = (): CreateLocationState => {
    return {
        createLocationRequest: buildDefaultCreateLocationRequest(),
        createLocationRequestStatus: RequestStatus.NONE,
        errorMessage: null
    }
}

const initialState: CreateLocationState = buildInitialState()


export const locationCreateSlice = createSlice({
    name: 'locationCreate',
    initialState,
    reducers: {
        clearCreateLocationState: () => {
            return buildInitialState();
        },
        updateWorkingHours: (state, {payload}: PayloadAction<WorkingHoursRequest & { fieldName: string }>) => {
            if (state.createLocationRequest.workSchedule && state.createLocationRequest.workSchedule[payload.fieldName as keyof typeof state.createLocationRequest.workSchedule]) {
                state.createLocationRequest.workSchedule[payload.fieldName as keyof typeof state.createLocationRequest.workSchedule].openTime = payload.openTime
                state.createLocationRequest.workSchedule[payload.fieldName as keyof typeof state.createLocationRequest.workSchedule].closeTime = payload.closeTime
                state.createLocationRequest.workSchedule[payload.fieldName as keyof typeof state.createLocationRequest.workSchedule].isClosed = payload.isClosed
            }
        },
        updateAftersalesWorkingHours: (state, {payload}: PayloadAction<WorkingHoursRequest & {
            fieldName: string
        }>) => {
            if (state.createLocationRequest && state.createLocationRequest.aftersalesWorkSchedule && state.createLocationRequest.aftersalesWorkSchedule[payload.fieldName as keyof typeof state.createLocationRequest.aftersalesWorkSchedule]) {
                // @ts-ignore
                state.createLocationRequest.aftersalesWorkSchedule[payload.fieldName as keyof typeof state.createLocationRequest.aftersalesWorkSchedule]!.openTime = payload.openTime
                // @ts-ignore
                state.createLocationRequest.aftersalesWorkSchedule[payload.fieldName as keyof typeof state.createLocationRequest.aftersalesWorkSchedule]!.closeTime = payload.closeTime
                // @ts-ignore
                state.createLocationRequest.aftersalesWorkSchedule[payload.fieldName as keyof typeof state.createLocationRequest.aftersalesWorkSchedule]!.isClosed = payload.isClosed
            }
        },
        updateCreateLocationRequest: (state, {payload}: PayloadAction<CreateLocationRequestPartial>) => {
            state.createLocationRequest = {
                ...state.createLocationRequest,
                ...payload
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(createLocation.pending, (state) => {
            state.createLocationRequestStatus = RequestStatus.PENDING;
            state.errorMessage = null;
        })
        builder.addCase(createLocation.fulfilled, (state, {payload}) => {
            state.createLocationRequestStatus = RequestStatus.RESOLVED;
        })
        builder.addCase(createLocation.rejected, (state, action) => {
            state.createLocationRequestStatus = RequestStatus.REJECTED;
            state.errorMessage = (action.payload as string | null) ?? null;
            state.createLocationRequest.publicId = v4();
        })
    },
});

const mapRequest = (request: CreateLocationRequestPartial): CreateLocationRequest => {
    const mappedRequest: CreateLocationRequest = {
        publicId: selectExceptionally(request.publicId),
        name: selectExceptionally(request.name),
        city: selectExceptionally(request.city),
        postcode: selectExceptionally(request.postcode),
        phoneNumber: selectExceptionally(request.phoneNumber),
        workSchedule: selectExceptionally(request.workSchedule),
        vehicleDataProvider: selectExceptionally(request.vehicleDataProvider),
        advertiserId: request.advertiserId,
        filePath: request.filePath,
        feedId: request.feedId,
        locationHash: request.locationHash,
        locationEmailTranscriptRecipients: prepareEmails(request.locationEmailTranscriptRecipients),
        locationEmailFiltrationEnabled: selectExceptionally(request.locationEmailFiltrationEnabled),
        locationEmailTranscriptSummaryEnabled: Boolean(request.locationEmailTranscriptSummaryEnabled),
        enquiryProvider: selectExceptionally(request.enquiryProvider),
        dragon2000DealerId: request.dragon2000DealerId,
        em2Region: request.em2Region,
        aftersalesEnabled: selectExceptionally(request.aftersalesEnabled),
        aftersalesEmailTranscriptRecipients: prepareEmails(request.aftersalesEmailTranscriptRecipients),
        aftersalesEmailFiltrationEnabled: selectExceptionally(request.aftersalesEmailFiltrationEnabled),
        aftersalesEmailTranscriptSummaryEnabled: Boolean(request.aftersalesEmailTranscriptSummaryEnabled),
        serviceBookingUrl: request.aftersalesEnabled ? selectExceptionally(request.serviceBookingUrl) : request.serviceBookingUrl,
        recallUrl: request.recallUrl || undefined,
        aftersalesWorkSchedule: selectExceptionally(request.aftersalesWorkSchedule),
        supportedVehicleManufacturers: request.supportedVehicleManufacturers || {},
    };
    return mappedRequest as CreateLocationRequest;
}

export const {
    updateWorkingHours,
    updateAftersalesWorkingHours,
    clearCreateLocationState,
    updateCreateLocationRequest,
} = locationCreateSlice.actions;

export const createLocationRequestSelector = (state: RootState): CreateLocationRequestPartial => state.locationCreate.createLocationRequest;
export const createLocationRequestStatusSelector = (state: RootState): RequestStatus => state.locationCreate.createLocationRequestStatus;
export const createLocationErrorMessageSelector = (state: RootState): string | null => state.locationCreate.errorMessage;
